import { useNavigate } from "react-router-dom";
import type { ColumnSchema, Row } from "../lib/types";
import { Cell } from "./Cell";

/**
 * The rows table. Shared by the full list screen and the related tabs on a
 * detail page — the two differ in what they fetch, not in how they render.
 */
export default function ResourceTable({
  resourceKey, columns, rows, isFetching, sort, direction, onSort, emptyMessage,
}: {
  resourceKey: string;
  columns: ColumnSchema[];
  rows: Row[];
  isFetching?: boolean;
  sort?: string;
  direction?: string;
  onSort?: (column: string) => void;
  emptyMessage?: string;
}) {
  const navigate = useNavigate();

  return (
    <div className={`panel overflow-x-auto transition-opacity ${isFetching ? "opacity-60" : ""}`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-ink-800">
            {columns.map((column) => (
              <th
                key={column.key}
                onClick={() => column.sortable && onSort?.(column.key)}
                className={`whitespace-nowrap px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-ink-500 ${
                  column.sortable && onSort ? "cursor-pointer hover:text-ink-300" : ""
                }`}
              >
                {column.label}
                {sort === column.key && (
                  <span className="ml-1 text-accent">{direction === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-850">
          {rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => navigate(`/r/${resourceKey}/${row.id}`)}
              className="cursor-pointer transition-colors hover:bg-ink-850/60"
            >
              {columns.map((column) => (
                <td key={column.key} className="whitespace-nowrap px-3 py-2 text-ink-300">
                  <Cell column={column} value={row[column.key]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {rows.length === 0 && (
        <div className="p-8 text-center text-sm text-ink-600">{emptyMessage || "Inga rader matchar."}</div>
      )}
    </div>
  );
}
